import type { ReactNode } from 'react';
import { RADIUS_KM, type SearchPrefs } from '@repo/shared/domain';
import { countryFlag } from '@repo/shared/geo';
import { canUseGenderFilter, canUseScope } from '@repo/shared/plans';
import { useElapsedSeconds } from '../hooks/useElapsedSeconds.ts';
import { capitalize, countryName, formatCount, searchSummary } from '../labels.ts';
import type { SessionError, SessionSnapshot } from '../lib/session.ts';
import { Icon, type IconName } from './Icon.tsx';
import { RotatingGreeting } from './Stage.tsx';

export interface StatusOverlayProps {
  snapshot: SessionSnapshot;
  prefs: SearchPrefs;
  needsLocation: boolean;
  locationProblem: string | null;
  onStart: () => void;
  onRecover: (patch: Partial<SearchPrefs>) => void;
  onRetryLocation: () => void;
  onOpenPlans: () => void;
  onExit: () => void;
}

/** Nach so vielen Sekunden Suche gibt es einen Tipp, die Filter zu lockern. */
const HINT_AFTER_SECONDS = 15;

/** Alles, was über dem Video liegt, solange kein Gespräch läuft. */
export function StatusOverlay(props: StatusOverlayProps) {
  const { snapshot, prefs, needsLocation, locationProblem } = props;
  const searchingFor = useElapsedSeconds(
    snapshot.phase === 'searching' ? snapshot.searchingSince : null,
  );

  if (needsLocation && locationProblem) {
    return (
      <Overlay>
        <Card icon="pin" title="Wo bist du gerade?" text={locationProblem}>
          <button type="button" className="button" onClick={props.onRetryLocation}>
            Nochmal versuchen
          </button>
          <button
            type="button"
            className="button"
            onClick={() => props.onRecover({ scope: 'national' })}
          >
            Mein Land
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={() => props.onRecover({ scope: 'global' })}
          >
            Weltweit
          </button>
        </Card>
      </Overlay>
    );
  }

  if (snapshot.error) {
    return (
      <Overlay>
        <ErrorCard {...props} error={snapshot.error} />
      </Overlay>
    );
  }

  if (needsLocation) {
    return (
      <Overlay>
        <div className="overlay__content">
          <div className="spinner" aria-hidden="true" />
          <p>Wir schauen kurz, wo du bist.</p>
        </div>
      </Overlay>
    );
  }

  switch (snapshot.phase) {
    case 'idle':
      return (
        <Overlay>
          <div className="overlay__content">
            <h2>Bereit für jemand Neuen?</h2>
            <p>{capitalize(searchSummary(prefs))}</p>
            <button
              type="button"
              className="button button--primary button--large"
              onClick={props.onStart}
            >
              <Icon name="play" /> Suche starten
            </button>
          </div>
        </Overlay>
      );
    case 'searching':
      return (
        <Overlay>
          <div className="overlay__content">
            <div className="seeker" aria-hidden="true">
              <span className="seeker__ring" />
              <span className="seeker__ring" />
              <span className="seeker__ring" />
              <span className="seeker__core">
                <Icon name="search" size={24} />
              </span>
            </div>
            {snapshot.signaling === 'open' && <RotatingGreeting />}
            <h2>
              {snapshot.signaling === 'open'
                ? 'Wir suchen jemanden für dich'
                : 'Einen Moment, wir verbinden dich'}
            </h2>
            <p>
              {capitalize(searchSummary(prefs))} · {formatCount(snapshot.online)} online
            </p>
            {searchingFor >= HINT_AFTER_SECONDS && (
              <SearchHint prefs={prefs} onRecover={props.onRecover} />
            )}
          </div>
        </Overlay>
      );
    case 'connecting':
      return (
        <Overlay>
          <div className="overlay__content">
            <h2>Gleich seid ihr verbunden</h2>
            {snapshot.peer?.country && (
              <p>
                {countryFlag(snapshot.peer.country)} {countryName(snapshot.peer.country)}
              </p>
            )}
            <span className="connecting-bar" aria-hidden="true" />
          </div>
        </Overlay>
      );
    case 'connected':
      return null;
  }
}

function Overlay({ children }: { children: ReactNode }) {
  return <div className="overlay">{children}</div>;
}

function Card({
  icon,
  title,
  text,
  children,
}: {
  icon: IconName;
  title: string;
  text: string;
  children: ReactNode;
}) {
  return (
    <div className="card" role="alert">
      <span className="card__icon">
        <Icon name={icon} size={22} />
      </span>
      <h2>{title}</h2>
      <p>{text}</p>
      <div className="card__actions">{children}</div>
    </div>
  );
}

function SearchHint({
  prefs,
  onRecover,
}: {
  prefs: SearchPrefs;
  onRecover: StatusOverlayProps['onRecover'];
}) {
  if (prefs.scope === 'local' && prefs.radiusKm < RADIUS_KM.max) {
    const wider = Math.min(RADIUS_KM.max, prefs.radiusKm * 2);
    return (
      <div className="hint-card">
        <p>In {prefs.radiusKm} km ist gerade niemand. Magst du weiter suchen?</p>
        <div className="card__actions">
          <button type="button" className="button" onClick={() => onRecover({ radiusKm: wider })}>
            {wider} km
          </button>
          <button type="button" className="button" onClick={() => onRecover({ scope: 'national' })}>
            Ganzes Land
          </button>
        </div>
      </div>
    );
  }
  if (prefs.genderFilter !== 'any') {
    return (
      <div className="hint-card">
        <p>Mit deinem Filter sind gerade nur wenige online.</p>
        <div className="card__actions">
          <button
            type="button"
            className="button"
            onClick={() => onRecover({ genderFilter: 'any' })}
          >
            Alle zeigen
          </button>
        </div>
      </div>
    );
  }
  if (prefs.scope === 'national') {
    return (
      <div className="hint-card">
        <p>In deinem Land ist gerade wenig los.</p>
        <div className="card__actions">
          <button type="button" className="button" onClick={() => onRecover({ scope: 'global' })}>
            Weltweit suchen
          </button>
        </div>
      </div>
    );
  }
  return <p className="hint">Gerade ist wenig los. Bleib einfach dran.</p>;
}

function ErrorCard({
  error,
  snapshot,
  prefs,
  ...actions
}: StatusOverlayProps & { error: SessionError }) {
  switch (error.code) {
    case 'plan_required':
      return (
        <Card icon="crown" title="Dafür brauchst du mehr" text={error.message}>
          <button
            type="button"
            className="button"
            onClick={() =>
              actions.onRecover({
                scope: canUseScope(snapshot.plan, prefs.scope) ? prefs.scope : 'global',
                genderFilter: canUseGenderFilter(snapshot.plan) ? prefs.genderFilter : 'any',
              })
            }
          >
            Ohne diese Filter
          </button>
          <button type="button" className="button button--primary" onClick={actions.onOpenPlans}>
            Tarife ansehen
          </button>
        </Card>
      );
    case 'location_required':
      return (
        <Card icon="pin" title="Wo bist du gerade?" text={error.message}>
          <button
            type="button"
            className="button"
            onClick={() => actions.onRecover({ scope: 'national' })}
          >
            Mein Land
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={() => actions.onRecover({ scope: 'global' })}
          >
            Weltweit
          </button>
        </Card>
      );
    case 'country_unknown':
      return (
        <Card
          icon="globe"
          title="Wir erkennen dein Land nicht"
          text={
            import.meta.env.DEV
              ? `${error.message} Tipp: Im DEV-Panel einen Test-Standort wählen.`
              : error.message
          }
        >
          <button
            type="button"
            className="button button--primary"
            onClick={() => actions.onRecover({ scope: 'global' })}
          >
            Weltweit suchen
          </button>
        </Card>
      );
    case 'banned':
    case 'too_many_connections':
    case 'connection_lost':
      return (
        <Card icon="shield" title="Die Verbindung ist beendet" text={error.message}>
          <button type="button" className="button" onClick={actions.onExit}>
            Zur Startseite
          </button>
        </Card>
      );
    default:
      return (
        <Card icon="close" title="Das hat nicht geklappt" text={error.message}>
          <button type="button" className="button button--primary" onClick={actions.onStart}>
            Nochmal versuchen
          </button>
        </Card>
      );
  }
}
